import React, { useState } from 'react';
import { SwiperProps, SwiperSlide } from 'swiper/react';

import 'swiper/css';
import 'swiper/css/autoplay';
import 'swiper/css/effect-fade';
import { Flex, Image, Icon, Label, Grid } from '@zeal/web-ui';
import { useTranslation } from 'react-i18next';
import { StyledSlider } from './styledComponents';
import { Autoplay, EffectFade } from 'swiper/modules';
import { useDashboardBranding } from '@modules/Shared/Components/Branded/useDashboardBranding';
import BrandedContent from '@modules/Shared/Components/Branded/BrandedContent';
import { TitleImage } from '@app/Components/Shared';
import ZealLogo from '@app/Components/Shared/ZealLogo';

export const APP_WEBSITE = import.meta.env.REACT_APP_WEBSITE ?? '';

const SLIDE_1_SRC =
	'https://zeal-public-assets.s3.eu-central-1.amazonaws.com/common/vendor_login_slide_1.png';
const SLIDE_1_SRC_BRANDED =
	'https://zeal-public-assets.s3.eu-central-1.amazonaws.com/common/vendor_login_slide_1_wl.png';
const SLIDE_2_SRC =
	'https://zeal-public-assets.s3.eu-central-1.amazonaws.com/common/vendor_login_slide_2.png';
const SLIDE_3_SRC =
	'https://zeal-public-assets.s3.eu-central-1.amazonaws.com/common/vendor_login_slide_3.png';
const SLIDE_4_SRC =
	'https://zeal-public-assets.s3.eu-central-1.amazonaws.com/common/vendor_login_slide_4.png';

const carouselImages = [
	<BrandedContent
		key="carousel-image-1"
		zealContent={
			<Image
				src={SLIDE_1_SRC}
				className="w-full h-fit"
				style={{
					objectFit: 'contain',
				}}
			/>
		}
		brandedContent={
			<Image
				src={SLIDE_1_SRC_BRANDED}
				className="w-full h-fit"
				style={{
					objectFit: 'contain',
				}}
			/>
		}
	/>,
	<Image
		key="carousel-image-2"
		src={SLIDE_2_SRC}
		className="w-full h-fit"
		style={{
			objectFit: 'contain',
		}}
	/>,
	<Image
		key="carousel-image-3"
		src={SLIDE_3_SRC}
		className="w-full h-fit"
		style={{
			objectFit: 'contain',
		}}
	/>,
	<Image
		key="carousel-image-4"
		src={SLIDE_4_SRC}
		className="h-fit"
		style={{
			width: '90%',
		}}
	/>,
];

const cardTitles = [
	'carousel_1_title',
	'carousel_2_title',
	'carousel_3_title',
	'carousel_4_title',
];

const cardSubtitles = [
	'carousel_1_subtitle',
	'carousel_2_subtitle',
	'carousel_3_subtitle',
	'carousel_4_subtitle',
];

export default function CarouselCard() {
	const { t } = useTranslation('magicLink');

	const { title, isBranded, i18nContext } = useDashboardBranding();

	const [swiperRef, setSwiperRef] = useState<SwiperProps | null>(null);
	const [slide, setSlide] = useState(0);

	return (
		<Flex isColumn gap="4" className="p-6 h-screen relative">
			<Flex
				className="relative"
				isColumn
				style={{
					height: '98%',
				}}
			>
				<div
					style={{
						position: 'absolute',
						top: 0,
						left: 0,
						width: '100%',
						height: '100%',
						background:
							'linear-gradient( 0deg, var(--color-primary-900) 0%, var(--color-primary-500) 100%)',
						zIndex: -1,
						borderRadius: '12px',
					}}
				/>
				<Flex
					justify="between"
					align="center"
					className="w-full px-12 py-10 absolute top-0"
				>
					<TitleImage
						style={{
							height: '29px',
						}}
					/>
					<a
						target="_blank"
						rel="noreferrer"
						href={APP_WEBSITE}
						className="z-10"
					>
						<Flex
							align="center"
							justify="center"
							className="cursor-pointer"
							style={{
								gap: '7px',
							}}
						>
							<Label.Mid300 variant="white">{t('learn_more')}</Label.Mid300>
							<Icon
								iconName="arrowRight"
								colorVariant="white"
								variant="regular"
							/>
						</Flex>
					</a>
				</Flex>
				<StyledSlider
					slidesPerView={1}
					loop
					autoplay={{
						delay: 20000,
						disableOnInteraction: false,
					}}
					speed={300}
					modules={[Autoplay, EffectFade]}
					effect="fade"
					fadeEffect={{
						crossFade: true,
					}}
					onSwiper={setSwiperRef}
					onSlideChange={(swiperCore: SwiperProps) => {
						setSlide(swiperCore?.activeIndex);
					}}
				>
					{[0, 1, 2, 3].map((index) => (
						<SwiperSlide key={index} data-testid="carousel-slide">
							<Flex
								isColumn
								gap={isBranded && index === 0 ? '10' : '3'}
								className="px-12 pt-12"
								align="center"
								style={{ width: '568px' }}
							>
								<Flex isColumn gap="3">
									<Label.Big600
										variant="white"
										textAlign="center"
										className="self-stretch"
									>
										{t(cardTitles[index], {
											title: title,
											context: i18nContext,
										})}
									</Label.Big600>
									<Label.Thin200
										variant="white"
										textAlign="center"
										style={{ width: '500px' }}
									>
										{t(cardSubtitles[index], {
											title: title,
											context: i18nContext,
										})}
									</Label.Thin200>
								</Flex>
								<BrandedContent
									brandedContent={
										<Flex
											align="center"
											justify="center"
											gap="2"
											className="w-full py-1"
											style={{
												background: 'rgba(0, 0, 0, 0.08)',
											}}
										>
											<ZealLogo fill="white" />
											<Label.Mid200 variant="white" textAlign="center">
												{t('powered_by_zeal', {
													ns: 'common',
												})}
											</Label.Mid200>
										</Flex>
									}
								/>
							</Flex>
							{carouselImages[index]}
						</SwiperSlide>
					))}
				</StyledSlider>
			</Flex>
			<Grid cols={4} gap="3" className="w-full">
				{[0, 1, 2, 3].map((index) => (
					<div
						key={index}
						className="h-2 rounded-lg cursor-pointer"
						data-testid={`pagination-button-${index}`}
						style={{
							background:
								slide === index
									? 'var(--color-primary-500)'
									: 'rgba(147, 147, 147, 0.20)',
						}}
						onClick={() => swiperRef?.slideTo(index)}
					/>
				))}
			</Grid>
		</Flex>
	);
}
